/**
 * iconUpload.js — Firebase Storage utility for custom icon uploads.
 * 
 * Images are stored at: users/{uid}/icons/{timestamp}_{filename}
 * Returns a public download URL after upload.
 */
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage, auth } from '../firebase/config';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif', 'image/svg+xml'];
const MAX_DIMENSION = 256; // Resize to max 256x256

/**
 * Resize an image file to fit within MAX_DIMENSION while maintaining aspect ratio.
 * Returns a Blob (always PNG for quality).
 */
function resizeImage(file) {
    return new Promise((resolve, reject) => {
        // SVGs don't need resizing
        if (file.type === 'image/svg+xml') {
            resolve(file);
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);
        
        img.onload = () => {
            URL.revokeObjectURL(url);
            
            let { width, height } = img;
            
            // Only resize if larger than max
            if (width <= MAX_DIMENSION && height <= MAX_DIMENSION) {
                resolve(file);
                return;
            }
            
            // Scale proportionally
            if (width > height) {
                height = Math.round((height * MAX_DIMENSION) / width);
                width = MAX_DIMENSION;
            } else {
                width = Math.round((width * MAX_DIMENSION) / height);
                height = MAX_DIMENSION;
            }
            
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to resize image'));
                    }
                },
                'image/png',
                0.9
            );
        };
        
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error('Failed to load image'));
        };
        
        img.src = url;
    });
}

/**
 * Upload an icon image to Firebase Storage.
 * 
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} The download URL
 * @throws {Error} On validation failure or upload error
 */
export async function uploadIconImage(file) {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error('Must be signed in to upload icons');
    
    // Validate type
    if (!ALLOWED_TYPES.includes(file.type)) {
        throw new Error(`Invalid file type. Allowed: PNG, JPG, WebP, GIF, SVG`);
    }
    
    // Validate size
    if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File too large. Maximum size: 2MB`);
    }
    
    // Resize image
    const resized = await resizeImage(file);
    
    // Generate unique filename
    const safeName = file.name
        .replace(/[^a-zA-Z0-9._-]/g, '_')
        .substring(0, 50);
    const fileName = `${Date.now()}_${safeName}`;
    
    // Upload to Firebase Storage
    const storageRef = ref(storage, `users/${uid}/icons/${fileName}`);
    const metadata = {
        contentType: resized.type || file.type,
        customMetadata: {
            originalName: file.name,
            uploadedAt: new Date().toISOString(),
        },
    };
    
    await uploadBytes(storageRef, resized, metadata);
    const downloadURL = await getDownloadURL(storageRef);
    
    return downloadURL;
}

/**
 * Delete an uploaded icon from Firebase Storage.
 * 
 * @param {string} downloadURL - The URL returned from uploadIconImage
 */
export async function deleteIconImage(downloadURL) {
    const uid = auth.currentUser?.uid;
    if (!uid) return;
    
    try {
        // Extract the storage path from the download URL
        const storageRef = ref(storage, downloadURL);
        await deleteObject(storageRef);
    } catch (err) {
        // Silently fail — the image may have already been deleted
        console.warn('Failed to delete icon image:', err.message);
    }
}
